import type { AdminMoneyHealthDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ClosingIcon, EscalatedIcon, PayoutFailIcon, WebhookIcon } from './icons';
import { tokens } from '../../../design';
import { countText } from '../_lib/moneyHealthText';

/**
 * The across-the-desk read: four numbers, three of them red.
 *
 * «Окно холда закрывается» is the one deliberately quiet tile. Those rows are
 * still owned by the automation; the operator watches them, they are not a
 * queue of work, and a fourth red number would flatten exactly the distinction
 * this screen exists to draw.
 *
 * Every value renders through `countText`, so a floor keeps its `≥`.
 */
const Signal = ({
  icon,
  value,
  label,
  sub,
  alert,
  testId,
}: {
  icon: ReactNode;
  value: string;
  label: string;
  sub: string;
  alert: boolean;
  testId: string;
}): ReactElement => (
  <Box
    data-testid={testId}
    sx={{
      flex: 1,
      minWidth: 0,
      px: 4,
      py: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      borderRight: `1px solid ${tokens.border.default}`,
      '&:last-of-type': { borderRight: 0 },
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 30,
        height: 30,
        borderRadius: '9px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: alert ? tokens.state.dangerBg : tokens.surface.muted,
        color: alert ? tokens.state.danger : tokens.text.tertiary,
        '& svg': { width: 16, height: 16 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 21,
          fontWeight: 700,
          lineHeight: '24px',
          letterSpacing: '-0.4px',
          color: alert ? tokens.state.danger : tokens.text.primary,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{ fontSize: 11, fontWeight: 600, lineHeight: '14px', color: tokens.text.secondary }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10, lineHeight: '13px', color: tokens.text.tertiary }}>
        {sub}
      </Typography>
    </Box>
  </Box>
);

export const SignalBar = ({ data }: { data: AdminMoneyHealthDto }): ReactElement => {
  const { t } = useTranslation();
  const escalated = countText(data.escalatedHolds);
  const closing = countText(data.closingHolds);
  const payouts = countText(data.failedPayouts);

  return (
    <Box
      sx={{
        display: 'flex',
        background: tokens.surface.base,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: `${tokens.radius.card}px`,
        boxShadow: tokens.shadow.card,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Signal
        testId="signal-escalated"
        icon={<EscalatedIcon />}
        value={t(escalated.key, { ...escalated.values })}
        label={t('moneyHealth.signals.escalated.label')}
        sub={t('moneyHealth.signals.escalated.sub')}
        alert={data.escalatedHolds.total > 0}
      />
      <Signal
        testId="signal-closing"
        icon={<ClosingIcon />}
        value={t(closing.key, { ...closing.values })}
        label={t('moneyHealth.signals.closing.label')}
        sub={t('moneyHealth.signals.closing.sub')}
        alert={false}
      />
      <Signal
        testId="signal-payouts"
        icon={<PayoutFailIcon />}
        value={t(payouts.key, { ...payouts.values })}
        label={t('moneyHealth.signals.payouts.label')}
        sub={t('moneyHealth.signals.payouts.sub')}
        alert={data.failedPayouts.total > 0}
      />
      <Signal
        testId="signal-webhooks"
        icon={<WebhookIcon />}
        value={String(data.stuckWebhooks.rowCount)}
        label={t('moneyHealth.signals.webhooks.label')}
        sub={t('moneyHealth.signals.webhooks.sub', {
          count: data.stuckWebhooks.byEventType.length,
        })}
        alert={data.stuckWebhooks.rowCount > 0}
      />
    </Box>
  );
};
